import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { cn } from "@/lib/utils"

/**
 * Enhanced Slider Component
 * Supports both single and dual-handle (range) sliders
 * 
 * P1 FIX: Added variant="min-threshold" for minimum threshold sliders
 * 
 * Variants:
 * - "default": colored from min (0) to thumb (standard selection)
 * - "min-threshold": colored from thumb to max (shows INCLUDED results for >= n filters)
 * - "range": colored between two thumbs (for budget-style range selection)
 * 
 * Usage:
 * - Budget slider (range): variant="range" or default with 2 values
 * - Experience/Procedures/Est (min threshold): variant="min-threshold"
 */
const Slider = React.forwardRef(({ className, value, defaultValue, variant = "default", max = 100, ...props }, ref) => {
  // Determine number of thumbs based on value array length
  const values = value || defaultValue || [0];
  const isRange = Array.isArray(values) && values.length > 1;
  const isMinThreshold = variant === "min-threshold";

  // Calculate percentage for min-threshold overlay
  const currentValue = Array.isArray(values) ? values[0] : values;
  const percentage = max > 0 ? (currentValue / max) * 100 : 0;

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full touch-none select-none items-center",
        className
      )}
      value={value}
      defaultValue={defaultValue}
      max={max}
      {...props}
    >
      {/* Track: base background */}
      <SliderPrimitive.Track className={cn(
        "relative h-2 w-full grow overflow-hidden rounded-full",
        isMinThreshold ? "bg-gradient-to-r from-blue-500 to-blue-600" : "bg-slate-200"
      )}>
        {isMinThreshold ? (
          /* Min-threshold variant: Gray from 0 to thumb, colored from thumb to max */
          /* We overlay a gray div from 0 to thumb position */
          <div 
            className="absolute h-full bg-slate-200 rounded-l-full"
            style={{ width: `${percentage}%`, left: 0 }}
          />
        ) : (
          /* Default/Range variant: Colored from min to thumb (or between thumbs) */
          <SliderPrimitive.Range className="absolute h-full bg-gradient-to-r from-blue-500 to-blue-600" />
        )}
      </SliderPrimitive.Track>
      {/* Render thumb for each value in the array */}
      {values.map((_, index) => (
        <SliderPrimitive.Thumb
          key={index}
          className="block h-5 w-5 rounded-full border-2 border-blue-600 bg-white shadow-lg ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:scale-110 cursor-grab active:cursor-grabbing"
        />
      ))}
    </SliderPrimitive.Root>
  );
});
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
